
import React, { useState, useEffect, useRef, useMemo } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import Swal from "sweetalert2";
import { apiFetch } from "./utils/api";
import { encodeFormUrl } from "./utils/urlEncoder";
import { decodeFormUrl } from "./utils/urlEncoder";
import styled from "styled-components";
import confetti from 'canvas-confetti';
import { FaGear } from "react-icons/fa6";
import { FaSignOutAlt } from "react-icons/fa";
import logo_eagle from "./assets/img/logo_eagle.png"
import help_desk from "./assets/img/help_desk.png";
import Settings from "./Settings";
import { useUnsavedChanges } from "./utils/UnsavedChangesContext";
import ActivityPanel from "./ActivityPanel";
import "./Form_builder_header.css";

const Navbar = styled.nav`
  background-color: #f8f9fa ;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 20px;
  border-bottom: 1px solid #ddd;
  position: relative;
  min-height: 60px;
`;

const RoundedCircle = styled.span`
  border-radius: 50%;
  text-align: center;
  font-size: 14px;
  line-height: 40px;
  display: inline-flex;
  justify-content: center;
  align-items: center;
`;

const Form_builder_header = ({ isSaveEnabled: isSaveEnabledProp, versionRefreshKey }) => {
    const navigate = useNavigate();
    // Leaving the builder with unsaved changes asks first (see
    // UnsavedChangesContext). On other screens this is plain navigation.
    const { guardedNavigate, confirmLeave, isDirty, saveStatus, setLiveInfo, saveNow, confirmLiveUpdate } = useUnsavedChanges();
    const isSaveEnabled = isSaveEnabledProp || isDirty;
    const location = useLocation();
    // Set while the builder shows an older saved version (from edit history).
    const viewingVersion = new URLSearchParams(location.search).get("version");

    const { token, template, deviceParams } = useParams();

    const decoded = useMemo(() => {
        return token ? decodeFormUrl(token) : null;
    }, [token]);

    const formId = decoded?.formId || null;
    const pageId = decoded?.pageId || null;
    const device = deviceParams?.replace("device-", "") || null;

    const [profileDetails, setProfileDetails] = useState(null);
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const dropdownRef = useRef(null);

    const [form, setForm] = useState(null);
    const [formPages, setFormPages] = useState([]);
    const [formTitle, setFormTitle] = useState("");
    const [renameFormId, setRenameFormId] = useState(null);

    const [edithistory, setEdithistory] = useState(false);
    const [showActivity, setShowActivity] = useState(false);
    const [versionHistory, setVersionHistory] = useState([]);

    const [selectedPage, setSelectedPage] = useState(null);
    const [showSettings, setShowSettings] = useState(false);

    const [isOpen, setIsOpen] = useState(false);

    const [isMobile, setIsMobile] = useState(false);
    const [hideNavbar, setHideNavbar] = useState(false);

    const [isFormOwner, setIsFormOwner] = useState(true);
    const [hasResponses, setHasResponses] = useState(false);
    const [responseCount, setResponseCount] = useState(0);
    const [publishing, setPublishing] = useState(false);
    const [toastMessage, setToastMessage] = useState("");

    useEffect(() => {
        if (formId) {
            CheckFormOwner();
        }
    }, [formId]); // ✅ Runs whenever formId changes — including after new form creation

    const CheckFormOwner = async () => {
        const response = await apiFetch(
            `/api/leftnavbar/check-form-owner/${formId}`,
            {
                method: "GET",
                credentials: "include",
            }
        );

        if (!response.ok) {
            throw new Error("Unauthorized");
        }

        const data = await response.json();
        setIsFormOwner(data.isOwner);
    };

    const populateProfileDetails = async () => {
        const response = await apiFetch('/api/leftnavbar/get-user-profile', {
            method: 'GET',
            credentials: 'include',
        });

        if (!response.ok) {
            throw new Error('Unauthorized');
        }

        const data = await response.json();
        setProfileDetails(data);
    };

    const fetchFormPages = async (formId) => {
        try {
            const res = await apiFetch(`/api/form_builder/get-form-pages/${formId}`, {
                method: "GET",
                headers: {
                    "Content-Type": "application/json",
                },
                credentials: "include"
            });

            const data = await res.json();
            if (res.ok) {
                setFormPages(data.pages || []);
            } else {
                Swal.fire("Error", data.error || "Unable to fetch pages", "error");
            }
        } catch (error) {
            console.error("❌ Error loading pages:", error);
        }
    };

    const fetchResponseCount = async () => {
        try {
            // ✅ Fetch Responses
            const response = await apiFetch(`/api/responses/get-responses/${formId}`,
                {
                    method: "GET",
                    credentials: "include",
                }
            );

            if (!response.ok) {
                throw new Error("Failed to fetch responses");
            }

            const data = await response.json();

            if (data) {
                // ✅ Enable share button only if responses exist
                setHasResponses(Array.isArray(data) && data.length > 0);
                setResponseCount(Array.isArray(data) ? data.length : 0);
            }

        } catch (error) {
            console.error("❌ Error loading pages:", error);
        }
    };

    useEffect(() => {
        const init = async () => {
            try {
                await populateProfileDetails();
                // No form yet (a new form, or "Use this template" before it is
                // named): nothing to check or load.
                if (formId) {
                    await CheckFormOwner();
                    fetchFormPages(formId);
                    fetchResponseCount();
                }
            } catch (error) {
                sessionStorage.setItem("redirectAfterLogin", location.pathname);
                navigate("/login");
            }
        };

        init();
    }, []);

    useEffect(() => {
        if (formPages.length > 0) {
            const foundPage = formPages.find(p => String(p.page_id) === String(pageId));
            setSelectedPage(foundPage || formPages[0]);
        }
    }, [formPages, pageId]);

    useEffect(() => {
        let isMounted = true;
        if (formId) {
            apiFetch(`/api/form_builder/get-forms-for-all?formId=${formId}`, {
                method: "GET",
                credentials: "include",
            })
                .then((response) => {
                    if (!response.ok) {
                        return response.text().then((text) => {
                            let errorData;
                            try {
                                errorData = JSON.parse(text);
                            } catch {
                                throw new Error("Invalid error response");
                            }
                        });
                    }

                    // Handle empty response
                    return response.text().then((text) => {
                        if (!text) {
                            throw new Error("Empty response from server");
                        }
                        return JSON.parse(text);
                    });
                })
                .then((data) => {
                    if (isMounted) {
                        setForm(data);
                    }
                })
                .catch((error) => {
                    Swal.fire("Error", error.message || "Something went wrong", "error");
                });

            return () => {
                isMounted = false;
            };
        }
    }, [formId]);

    useEffect(() => {
        let isMounted = true;
        if (formId) {
            apiFetch(`/api/form_builder/${formId}/${pageId}/version-history`, {
                method: "GET",
                credentials: "include",
            })
                .then((response) => response.json())
                .then((data) => {
                    if (isMounted) {
                        setVersionHistory(data);
                    }
                })
                .catch((error) => {
                    Swal.fire("Error", error.message || "Something went wrong", "error");
                });

            return () => {
                isMounted = false;
            };
        }
    }, [formId, pageId, versionRefreshKey]);

    const handleStarredForm = async (formId, starred) => {
        const newStarredStatus = !starred;

        try {
            const response = await apiFetch(`/api/form_builder/toggle-star/${formId}`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                credentials: "include",
                body: JSON.stringify({ starred: newStarredStatus }),
            });

            const result = await response.json();
            if (response.ok) {
                Swal.fire({
                    icon: "success",
                    title: `Form ${newStarredStatus ? "starred" : "unstarred"}!`,
                    timer: 1200,
                    showConfirmButton: false,
                });
            } else {
                Swal.fire("Error", result.error, "error");
            }
        } catch (err) {
            Swal.fire("Error", "Something went wrong", "error");
        }
    };

    // Tell the save guard whether this form is live (and how many answered),
    // so saving asks before changing what people see on the link.
    const isPublished = form?.published === 1;
    useEffect(() => {
        setLiveInfo({ published: isPublished, responses: responseCount });
    }, [isPublished, responseCount, setLiveInfo]);

    // The Publish button, by state:
    //   not published, saved      -> Publish
    //   not published, unsaved    -> Save and publish (saves, then publishes)
    //   published, unsaved        -> Update live form (asks, then saves)
    //   published, saved          -> Published (nothing to do)
    const handlePublishClick = async () => {
        if (publishing) return;
        setPublishing(true);
        try {
            if (isPublished) {
                if (!isSaveEnabled) return;
                if (!(await confirmLiveUpdate())) return;
                if (await saveNow()) {
                    Swal.fire({ toast: true, position: "top-end", icon: "success", title: "Live form updated", showConfirmButton: false, timer: 2000 });
                }
                return;
            }
            if (isSaveEnabled && !(await saveNow())) return;
            await publishForm();
        } finally {
            setPublishing(false);
        }
    };

    const publishForm = async () => {
        if (!formId) {
            Swal.fire("Error", "Please save the form before publishing.", "error");
            return;
        }

        try {
            // ✅ Send publish request
            const response = await apiFetch(`/api/form_builder/publish-form/${formId}`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ published: true }),
                credentials: "include"
            });

            if (!response.ok) {
                throw new Error('Failed to publish form');
            }

            // The button now shows "Published" without a reload.
            setForm(prev => (prev ? { ...prev, published: 1 } : prev));

            // ✅ Construct public URL 
            const publicUrl = `${window.location.origin}/forms/${encodeFormUrl(formId, "start")}`;

            // ✅ Copy link to clipboard
            if (document.hasFocus()) {
                await navigator.clipboard.writeText(publicUrl);
            } else {
                console.warn("Clipboard copy skipped because the document is not focused.");
            }

            confetti({
                particleCount: 200,
                spread: 200,
                origin: { y: 0.6 }
            });

            // ✅ Success message
            Swal.fire({
                title: "Success!",
                html: `Form published successfully! <br> The link has been copied to clipboard: <br> <b>${publicUrl}</b>`,
                icon: "success"
            });

        } catch (error) {
            console.error("Error publishing form:", error);
            Swal.fire("Error", "Failed to publish the form.", "error");
        }
    };

    const handleRenameSubmit = (formId) => {
        if (!formTitle.trim()) {
            Swal.fire("Error", "Form title cannot be empty.", "error");
            return;
        }

        Swal.fire({
            title: "Confirm Rename",
            text: "Do you want to rename this form?",
            icon: "question",
            showCancelButton: true,
            confirmButtonText: "Yes, Rename",
            cancelButtonText: "Cancel",
        }).then((result) => {
            if (result.isConfirmed) {
                apiFetch(`/api/form_builder/rename-form/${formId}`, {
                    method: "PUT",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({ title: formTitle }),
                    credentials: "include",
                })
                    .then((res) => {
                        if (!res.ok) {
                            return res.json().then(err => { throw new Error(err.error || "Failed to rename form"); });
                        }
                        return res.json();
                    })
                    .then(() => {
                        // ✅ Update UI immediately
                        setForm(prev => ({ ...prev, title: formTitle }));

                        // ✅ Close modal
                        const modal = document.getElementById('exampleModalCenter');
                        if (modal) {
                            modal.classList.remove("show");
                            modal.style.display = "none";
                        }
                        document.body.classList.remove("modal-open");
                        document.querySelectorAll(".modal-backdrop").forEach((el) => el.remove());

                        Swal.fire("Renamed!", "Your form has been renamed.", "success");
                    })
                    .catch((error) => {
                        console.error("Error renaming form:", error);
                        if (error.message.includes("already exists")) {
                            Swal.fire("Duplicate Title", "A form with this title already exists. Please choose a different name.", "warning");
                        } else {
                            Swal.fire("Error", error.message, "error");
                        }
                    });
            }
        });
    };

    const handleCopyEmail = (email) => {
        navigator.clipboard.writeText(email)
            .then(() => {
                Swal.fire({
                    icon: 'success',
                    title: 'Copied!',
                    text: `${email} has been copied to clipboard`,
                    timer: 1500,
                    showConfirmButton: false
                });
            })
            .catch(() => {
                Swal.fire({
                    icon: 'error',
                    title: 'Oops...',
                    text: 'Failed to copy email!'
                });
            });
    };

    const handleLogout = async () => {
        if (!(await confirmLeave())) return;
        try {
            const response = await apiFetch('/api/login/logout', {
                method: 'POST',
                credentials: 'include',
            });

            if (response.ok) {
                localStorage.clear();
                sessionStorage.clear();
                navigate('/login');
            } else {
                Swal.fire("Logout failed!");
            }
        } catch (error) {
            Swal.fire("An error occurred while logging out.");
            console.error(error);
        }
    };

    function formatDate(datetimeStr) {
        const date = new Date(datetimeStr);
        return date.toLocaleString('en-IN', {
            dateStyle: 'medium',
            timeStyle: 'short',
        });
    }

    useEffect(() => {
        const handleResize = () => {
            const screenWidth = window.innerWidth;
            const isMobileCondition = screenWidth < 768;
            setIsMobile(isMobileCondition);

            // ✅ check if URL contains /form-builder/ OR /workflow
            const path = window.location.pathname.toLowerCase();
            const isSpecialPage =
                path.includes("/form-builder/") || path.includes("/workflow") || path.includes("/share") || path.includes("/preview");

            setHideNavbar(isMobileCondition && isSpecialPage);
        };

        handleResize(); // run once on mount
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsDropdownOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    if (hideNavbar) return null;

    return (
        <Navbar className="form_builder_header">
            <div className="form_builder_header-left">
                <a
                    className={`home-icon ${!isFormOwner ? "disabled-link" : ""}`}
                    href={isFormOwner ? "/home" : "#"}
                    onClick={(e) => {
                        e.preventDefault();
                        if (isFormOwner) guardedNavigate("/home");
                    }}
                >
                    <i className="fa fa-home"></i>
                </a>

                {edithistory && (
                    <div className="edithistory-modal-backdrop">
                        <div className="edithistory-modal-box">
                            <button className="edithistory-close-btn" onClick={() => setEdithistory(false)}>
                                <i className="fa-solid fa-xmark"></i>
                            </button>
                            <h5 className="edithistory-modal-title">Form edit history</h5>
                            <p>
                                {selectedPage ? `Page: ${selectedPage.page_title || `Page ${selectedPage.page_number}`}. ` : ""}
                                Newest first. Open a version to see it; you can restore it from there.
                            </p>

                            <div className="version-list">
                                {Array.isArray(versionHistory) && versionHistory.length === 0 && (
                                    <p className="version-empty">No saved versions of this page yet.</p>
                                )}
                                {Array.isArray(versionHistory) && versionHistory.map((item) => {
                                    const viewing = viewingVersion
                                        ? Number(viewingVersion) === Number(item.fields_version)
                                        : item.latest;
                                    const changes = Array.isArray(item.changes) ? item.changes : [];
                                    return (
                                        <button
                                            type="button"
                                            className={`version-item ${viewing ? "version-item-current" : ""}`}
                                            key={item.fields_version}
                                            aria-current={viewing ? "true" : undefined}
                                            onClick={() => {
                                                setEdithistory(false);
                                                const base = `/form-builder/view/${encodeFormUrl(formId, pageId || "start")}`;
                                                if (item.latest) {
                                                    if (viewingVersion) guardedNavigate(base);
                                                } else {
                                                    guardedNavigate(`${base}?version=${item.fields_version}`);
                                                }
                                            }}
                                        >
                                            <div
                                                className="version-user-icon"
                                                style={{
                                                    backgroundColor: profileDetails?.profile_color || "#ccc"
                                                }}
                                            >
                                                {profileDetails?.profile_letters || "U"}
                                            </div>

                                            <div className="version-details">
                                                <span className="version-head">
                                                    <span className="version-time">{formatDate(item.created_at)}</span>
                                                    {item.latest && <span className="version-badge">Latest</span>}
                                                    {viewing && !item.latest && <span className="version-badge version-badge-viewing">Viewing</span>}
                                                </span>
                                                <span className="user-name">
                                                    {profileDetails
                                                        ? `${profileDetails.emp_first_name} ${profileDetails.emp_last_name}`
                                                        : "Loading..."}
                                                </span>
                                                {changes.length > 0 && (
                                                    <ul className="version-changes">
                                                        {changes.slice(0, 4).map((change, i) => <li key={i}>{change}</li>)}
                                                        {changes.length > 4 && <li className="version-more">and {changes.length - 4} more</li>}
                                                    </ul>
                                                )}
                                            </div>
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                    </div>
                )}

                {form && (
                    <div className="form_builder_header-form-title-row">
                        <p
                            className={`form_builder_header-form-title-text  ${!isFormOwner ? "disabled-link" : ""}`}
                            {...(isFormOwner && {
                                "data-bs-toggle": "modal",
                                "data-bs-target": "#exampleModalCenter"
                            })}
                            onClick={() => {
                                if (!isFormOwner) return;

                                setFormTitle(form.title);
                                setRenameFormId(form.form_id);
                            }}
                        >
                            {form.title}
                        </p>

                        {saveStatus && (
                            <span
                                className={`form_builder_header-save-status ${saveStatus}`}
                                role="status"
                                aria-live="polite"
                            >
                                {saveStatus === "unsaved" ? "Unsaved changes" : "All changes saved"}
                            </span>
                        )}

                        {form.starred ? (
                            <i
                                className="fa-solid fa-star form-star-icon"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleStarredForm(form.form_id, form.starred);
                                }}
                                style={{ color: "#ffc738", marginLeft: "5px", fontSize: "0.7rem" }}
                            ></i>
                        ) : null}
                    </div>
                )}

            </div>

            <div className="form_builder_header-center">
                {window.location.pathname.includes("preview") ? (
                    <div className="device-toggle">
                        <span
                            className={`center-nav-btn ${window.location.pathname.includes("device-desktop") ? "active" : ""}`}
                            onClick={() => guardedNavigate(`/preview/${encodeFormUrl(formId, pageId)}/device-desktop`)}
                        >
                            Desktop
                        </span>
                        <span
                            className={`center-nav-btn ${window.location.pathname.includes("device-mobile") ? "active" : ""}`}
                            onClick={() => guardedNavigate(`/preview/${encodeFormUrl(formId, pageId)}/device-mobile`)}
                        >
                            Mobile
                        </span>
                    </div>
                ) : (
                    <>
                        {isMobile ? (
                            // ✅ Mobile → show only Results
                            <span
                                className={`center-nav-btn ${window.location.pathname.includes("responses") ? "active" : ""}`}
                                onClick={() => guardedNavigate(`/responses/${encodeFormUrl(formId, "responses")}`)}
                            >
                                Results
                            </span>
                        ) : (
                            // ✅ Desktop → show all options
                            <>
                                <span
                                    className={`center-nav-btn 
                                        ${window.location.pathname.includes("form-builder") ? "active" : ""} ${!isFormOwner ? "disabled-link" : ""}`
                                    }
                                    onClick={() => {
                                        if (isFormOwner) {
                                            guardedNavigate(`/form-builder/view/${encodeFormUrl(formId, "start")}`);
                                        }
                                    }}
                                >
                                    Edit
                                </span>
                                <span
                                    className={`center-nav-btn ${window.location.pathname.includes("workflow") ? "active" : ""} ${!isFormOwner ? "disabled-link" : ""}`}
                                    onClick={() => {
                                        if (isFormOwner) {
                                            guardedNavigate(`/workflow/${encodeFormUrl(formId, "workflow")}`);
                                        }
                                    }}
                                >
                                    Integrate
                                </span>
                                <span
                                    className={`center-nav-btn ${window.location.pathname.includes("share") ? "active" : ""} ${!isFormOwner ? "disabled-link" : ""}`}
                                    onClick={() => {
                                        if (isFormOwner) {
                                            guardedNavigate(`/share/${encodeFormUrl(formId, "share")}`);
                                        }
                                    }}
                                >
                                    Share
                                </span>
                                <span
                                    className={`center-nav-btn ${window.location.pathname.includes("responses") ? "active" : ""} ${!isFormOwner ? "disabled-link" : ""}`}
                                    onClick={() => {
                                        if (isFormOwner) {
                                            guardedNavigate(`/responses/${encodeFormUrl(formId, "responses")}`);
                                        }
                                    }}
                                >
                                    Results
                                </span>
                            </>
                        )}
                    </>
                )}
            </div>

            <div className="form_builder_header-right">
                {window.location.pathname.includes("form-builder") && (
                    <button
                        className="form_builder_header-clock-btn"
                        style={{ marginLeft: "-5px" }}
                        onClick={() => setEdithistory(true)}
                    >
                        <i className="fa-regular fa-clock"></i>
                    </button>
                )}

                {window.location.pathname.includes("form-builder") && isFormOwner && formId && (
                    <button
                        type="button"
                        className="form_builder_header-activity-btn"
                        title="Activity"
                        aria-label="Activity"
                        onClick={() => setShowActivity(true)}
                    >
                        <i className="fa-solid fa-list-ul"></i>
                    </button>
                )}
                {showActivity && <ActivityPanel formId={formId} onClose={() => setShowActivity(false)} />}

                {/* Logo before profile */}
                <a
                    className={`${!isFormOwner ? "disabled-link" : ""}`}
                    href={isFormOwner ? "/home" : "#"}
                    style={{
                        display: "flex",
                        alignItems: "center",
                        pointerEvents: isFormOwner ? "auto" : "none",
                        opacity: isFormOwner ? 1 : 0.5,
                        cursor: isFormOwner ? "pointer" : "not-allowed"
                    }}
                    onClick={(e) => {
                        e.preventDefault();
                        if (isFormOwner) guardedNavigate("/home");
                    }}
                >
                    <img
                        src={logo_eagle}
                        alt="Home Logo"
                        style={{
                            height: "35px",
                            objectFit: "contain"
                        }}
                    />
                </a>

                {!window.location.pathname.includes("preview") && (
                    <div
                        className="form_builder_header-profile-section-wrapper "
                        style={{ marginRight: "5px" }}
                        onClick={() => setIsDropdownOpen(!isDropdownOpen)}>
                        {profileDetails ? (
                            <>
                                <RoundedCircle
                                    className="form_builder_header-profile-img"
                                    style={{
                                        backgroundColor: profileDetails.profile_color,
                                        color: 'white',
                                        fontSize: '18px',
                                        fontWeight: 'bold',
                                    }}
                                >
                                    {profileDetails.profile_letters}
                                </RoundedCircle>
                                {isDropdownOpen && (
                                    <div className="fb-profile-dd">
                                        <div className="fb-profile-dd-header">
                                            <div className="fb-profile-dd-name">
                                                {profileDetails.emp_first_name} {profileDetails.emp_last_name}
                                            </div>
                                            <div className="fb-profile-dd-tagline">
                                                Your Form, Your Space
                                            </div>
                                        </div>

                                        <div
                                            className="fb-profile-dd-item"
                                            onClick={() => handleCopyEmail("admin@dolluzcorp.com")}
                                        >
                                            <img src={help_desk} alt="Help" />
                                            admin@dolluzcorp.com
                                        </div>

                                        <a
                                            href="/login?changePassword"
                                            className="fb-profile-dd-item"
                                            onClick={(e) => {
                                                e.preventDefault();
                                                guardedNavigate("/login?changePassword");
                                            }}
                                        >
                                            <i className="fa fa-key"></i>
                                            Change Password
                                        </a>

                                        <div className="fb-profile-dd-divider"></div>

                                        <div
                                            className="fb-profile-dd-item fb-profile-dd-logout"
                                            onClick={handleLogout}
                                        >
                                            <FaSignOutAlt /> Logout
                                        </div>
                                    </div>
                                )}

                            </>
                        ) : (
                            "Loading..."
                        )}
                    </div>
                )}

                {!window.location.pathname.includes("responses") && (
                    <>
                        <div className="divider" />
                        <button
                            className="form_builder_header-setting-btn"
                            style={{ marginLeft: "-5px" }}
                            onClick={() => setShowSettings(true)}
                        >
                            <FaGear />
                        </button>
                    </>
                )}

                <div className="action-btns">
                    {(window.location.pathname.includes("form-builder") || window.location.pathname.includes("share")) && (
                        <button
                            className="form_builder_header-preview-btn"
                            onClick={() => guardedNavigate(`/preview/${encodeFormUrl(formId, "start")}/device-desktop`, { preview: true })}
                        >
                            Preview
                        </button>
                    )}

                    {window.location.pathname.includes("form-builder") && form && (
                        <button
                            className={`form_builder_header-publish-btn ${isPublished && !isSaveEnabled ? 'dimmed' : ''}`}
                            onClick={handlePublishClick}
                            disabled={(isPublished && !isSaveEnabled) || publishing}
                            title={isPublished && !isSaveEnabled ? "This form is live and has no unsaved changes" : undefined}
                        >
                            {isPublished
                                ? (isSaveEnabled
                                    ? <>Update live form <i className="fa-solid fa-bolt"></i></>
                                    : <>Published <i className="fa-solid fa-check"></i></>)
                                : (isSaveEnabled
                                    ? <>Save and publish <i className="fa-solid fa-bolt"></i></>
                                    : <>Publish <i className="fa-solid fa-bolt"></i></>)}
                        </button>
                    )}

                    {window.location.pathname.includes("preview") && (
                        <div style={{ display: "flex", alignItems: "center", gap: "1rem" }}>
                            <div className="form_builder_page-dropdown" style={{ position: 'relative' }}>
                                <div
                                    className="form_builder_page-select"
                                    onClick={() => setIsOpen(!isOpen)}
                                    style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
                                >
                                    {selectedPage ? (
                                        <span>
                                            <i className="fa-solid fa-file-alt recently-viewed-icon" style={{ marginRight: '6px', color: "rgb(245, 158, 11)" }}></i>
                                            {selectedPage.page_title?.trim() !== ""
                                                ? selectedPage.page_title
                                                : `Page ${selectedPage.page_number}`}
                                        </span>
                                    ) : (
                                        "Loading..."
                                    )}
                                    <i className={`fa-solid ${isOpen ? "fa-chevron-up" : "fa-chevron-down"}`}></i>
                                </div>

                                {isOpen && selectedPage && (
                                    <div
                                        className="form_builder_page-options"
                                        style={{
                                            position: 'absolute',
                                            top: '100%',
                                            left: 0,
                                            right: 0,
                                            backgroundColor: 'white',
                                            border: '1px solid #ccc',
                                            borderRadius: '6px',
                                            marginTop: '4px',
                                            zIndex: 1000,
                                        }}
                                    >
                                        {[...formPages]
                                            .sort((a, b) => a.sort_order - b.sort_order) // Ensure sorted order
                                            .map((page) => (
                                                <div
                                                    key={page.id}
                                                    className="form_builder_page-option"
                                                    style={{
                                                        padding: '8px 12px',
                                                        cursor: 'pointer',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        backgroundColor: page.page_id === selectedPage.page_id ? '#2563eb' : 'white',
                                                        color: page.page_id === selectedPage.page_id ? 'white' : 'black',
                                                    }}
                                                    onClick={() => {
                                                        setSelectedPage(page);
                                                        setIsOpen(false);
                                                        guardedNavigate(`/preview/${encodeFormUrl(formId, page.page_id)}/device-${device}`);
                                                    }}
                                                >
                                                    <i className="fa-solid fa-file-alt recently-viewed-icon" style={{ marginRight: '6px', color: "rgb(245, 158, 11)" }}></i>
                                                    {page.page_title?.trim() !== "" ? page.page_title : `Page ${page.page_id}`}
                                                </div>
                                            ))}
                                    </div>
                                )}
                            </div>

                            <button
                                className="form_builder_header-publish-btn"
                                style={{ fontSize: "0.9rem", fontWeight: "500" }}
                                onClick={() => guardedNavigate(`/form-builder/view/${encodeFormUrl(formId, "start")}`)}
                            >
                                <i className="fa-solid fa-xmark"></i> Exit preview
                            </button>
                        </div>
                    )}

                    {window.location.pathname.includes("responses") && (
                        <>
                            <button
                                className={`form_builder_header-publish-btn ${(!hasResponses || !isFormOwner) ? "disabled-btn" : ""
                                    }`}
                                disabled={!hasResponses || !isFormOwner}
                                onClick={() => {
                                    if (hasResponses && isFormOwner) {
                                        navigator.clipboard.writeText(
                                            `${window.location.origin}/responses/${encodeFormUrl(formId, "responses")}`
                                        );

                                        setToastMessage("Copied link to clipboard!");

                                        setTimeout(() => setToastMessage(""), 5000);
                                    }
                                }}
                            >
                                <i className="fa-solid fa-users"></i> Share result
                            </button>

                            {toastMessage && (
                                <div className="toast-popup">
                                    <div className="toast-icon-container">
                                        <span className="toast-icon"><i className="fa-solid fa-circle-check"></i></span>
                                    </div>
                                    <div className="toast-text">{toastMessage}</div>
                                    <button className="toast-close" onClick={() => setToastMessage("")}>
                                        &times;
                                    </button>
                                </div>
                            )}
                        </>
                    )}
                </div>
            </div>

            {form && (
                <div className="modal fade" id="exampleModalCenter" tabIndex="-1" role="dialog" aria-hidden="true" >
                    <div className="modal-dialog modal-dialog-centered" role="document">
                        <div className="modal-content custom-modal p-0">
                            <div className="modal-header border-0 pb-0">
                                <h5 className="modal-title">Name your form</h5>
                                <button type="button" className="btn-close" style={{ outline: "none", border: "none", boxShadow: "none" }} data-bs-dismiss="modal" aria-label="Close"><i className="fa-solid fa-xmark"></i></button>
                            </div>
                            <div className="modal-body pt-2">
                                <input
                                    type="text"
                                    id="formNameInput"
                                    className="form-control custom-input"
                                    value={formTitle}
                                    onChange={(e) => setFormTitle(e.target.value)}
                                />
                            </div>
                            <div className="modal-footer border-0 pt-0 justify-content-end">
                                <button
                                    type="button"
                                    id="continueButton"
                                    className="btn btn-primary custom-continue-btn"
                                    onClick={() => handleRenameSubmit(renameFormId)}
                                >
                                    Continue
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {showSettings && (
                <div className="settings-overlay">
                    <div className="settings-modal">
                        <button
                            className="settings-close"
                            onClick={() => setShowSettings(false)}
                        >
                            ✕
                        </button>
                        <Settings />
                    </div>
                </div>
            )}

        </Navbar>
    );
};

export default Form_builder_header;
